import {
  SYNONYM_EXPANSIONS,
  INTENT_PHRASES,
  CONTENT_TYPE_ALIASES,
  CATEGORY_ALIASES,
  expandSynonymTerm,
  expandTermsFromFullQuery,
  extractIntentCore
} from './intentMappings';
import { tokenizeQuery } from './buildSearchDocument';
import { matchesLocationQuery } from '../../utils/experienceLocation';

/**
 * Parse a natural-language search query into tokens, expanded terms, and constraints.
 * Full query is analyzed before any token is discarded — conversational filler
 * is stripped only to reveal intent, not to filter results early.
 *
 * @param {string} query
 * @returns {import('./experienceSearch').ParsedQuery}
 */
export function parseSearchQuery(query) {
  const raw = String(query || '').trim();
  const normalized = raw.toLowerCase();

  if (!normalized) {
    return {
      raw,
      normalized: '',
      intentCore: '',
      tokens: [],
      expandedTerms: [],
      constraints: {},
      categoryHints: [],
      contentTypeHints: []
    };
  }

  const intentCore = extractIntentCore(normalized);
  const expandedTerms = new Set();
  const constraints = {};
  const categoryHints = new Set();
  const contentTypeHints = new Set();

  // 1. Full-query intent phrases first (before tokenization)
  for (const phrase of INTENT_PHRASES) {
    if (phrase.test.test(normalized)) {
      (phrase.terms || []).forEach((t) => expandedTerms.add(t.toLowerCase()));
      if (phrase.constraints) {
        Object.assign(constraints, phrase.constraints);
      }
      const priceMatch = normalized.match(/under\s*\$?\s*(\d+)|less\s*than\s*\$?\s*(\d+)/i);
      if (phrase.constraints?.maxPrice && priceMatch) {
        constraints.maxPriceAmount = parseInt(priceMatch[1] || priceMatch[2], 10);
      }
    }
  }

  // 2. Scan entire query for synonym keys (catches "friends" even without "with")
  expandTermsFromFullQuery(normalized).forEach((t) => expandedTerms.add(t));
  if (intentCore !== normalized) {
    expandTermsFromFullQuery(intentCore).forEach((t) => expandedTerms.add(t));
  }

  // 3. Tokenize and expand — filler words removed from tokens only, not from intent
  const tokens = tokenizeQuery(raw);
  const coreTokens = tokenizeQuery(intentCore);

  for (const token of [...tokens, ...coreTokens]) {
    expandedTerms.add(token);
    expandSynonymTerm(token).forEach((t) => expandedTerms.add(t));

    if (CATEGORY_ALIASES[token]) {
      categoryHints.add(CATEGORY_ALIASES[token]);
    }
    if (CONTENT_TYPE_ALIASES[token]) {
      contentTypeHints.add(CONTENT_TYPE_ALIASES[token]);
    }
  }

  // Multi-word category / content type in full query
  for (const [alias, category] of Object.entries(CATEGORY_ALIASES)) {
    if (alias.includes(' ') && normalized.includes(alias)) {
      categoryHints.add(category);
      alias.split(/\s+/).forEach((w) => expandedTerms.add(w));
    }
  }

  // Bigrams from both token sets
  const tokenSets = [tokens, coreTokens];
  for (const set of tokenSets) {
    for (let i = 0; i < set.length - 1; i += 1) {
      const bigram = `${set[i]} ${set[i + 1]}`;
      if (SYNONYM_EXPANSIONS[bigram]) {
        SYNONYM_EXPANSIONS[bigram].forEach((t) => expandedTerms.add(t));
      }
      expandSynonymTerm(bigram).forEach((t) => expandedTerms.add(t));
      if (CATEGORY_ALIASES[bigram]) categoryHints.add(CATEGORY_ALIASES[bigram]);
      if (CONTENT_TYPE_ALIASES[bigram]) contentTypeHints.add(CONTENT_TYPE_ALIASES[bigram]);
    }
  }

  // Keep full query and intent core for phrase-level matching in ranker
  expandedTerms.add(normalized);
  if (intentCore) expandedTerms.add(intentCore);

  return {
    raw,
    normalized,
    intentCore,
    tokens: [...new Set([...tokens, ...coreTokens])],
    expandedTerms: [...expandedTerms],
    constraints,
    categoryHints: [...categoryHints],
    contentTypeHints: [...contentTypeHints]
  };
}

/** Check if a location hint from the query matches an experience. */
export function locationMatchesHint(doc, locationHint) {
  if (!locationHint) return false;
  return matchesLocationQuery(doc.experience, locationHint);
}
